import type { CardViewModel } from '@pesto/features/src/Cards/types/Cards.types';

import { toCardViewModel } from '@pesto/features/src/Cards/utils/toCardViewModel';

import type { GetLoanByIdQuery } from '../../__generated__/graphql/api';

type GeneratedLoan = NonNullable<GetLoanByIdQuery['loan']>;

type GeneratedCard = NonNullable<NonNullable<GeneratedLoan['cards']>[number]>;

type GeneratedUser = NonNullable<NonNullable<GeneratedLoan['users']>[number]>;

export interface LoanUserListItem {
  id: string;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  dob: string | null;
  isFriendsAndFam: boolean | null;
  insertedAt: string | null;
}

export interface LoanDetailViewModel {
  id: string;
  partnerAccountNumber: string | null;
  status: string | null;
  programName: string | null;

  creditLimit: number | null;
  initialBalance: number | null;
  originationDate: string | null;
  promoEndDate: string | null;
  insertedAt: string | null;
  aprMargin: string | null;
  currentApr: string | null;
  currentPrimeRate: string | null;
  isVariableApr: boolean | null;

  isDelinquent: boolean | null;
  daysDelinquent: number | null;
  delinquentSince: string | null;

  gracePeriodDays: number | null;

  users: LoanUserListItem[];
  cards: CardViewModel[];
}

const toLoanUserListItem = (user: GeneratedUser): LoanUserListItem => ({
  id: user.id ?? '',
  firstName: user.firstName ?? null,
  middleName: user.middleName ?? null,
  lastName: user.lastName ?? null,
  email: user.email ?? null,
  phone: user.phone ?? null,
  dob: user.dob ?? null,
  isFriendsAndFam: user.isFriendsAndFam ?? null,
  insertedAt: user.insertedAt ?? null,
});

export const toLoanDetail = (loan: GeneratedLoan): LoanDetailViewModel => ({
  id: loan.id ?? '',
  partnerAccountNumber: loan.partnerAccountNumber ?? null,
  status: loan.status ?? null,
  programName: loan.program?.name ?? null,

  creditLimit: loan.creditLimit ?? null,
  initialBalance: loan.initialBalance ?? null,
  originationDate: loan.originationDate ?? null,
  promoEndDate: loan.promoEndDate ?? null,
  insertedAt: loan.insertedAt ?? null,
  aprMargin: loan.aprMargin ?? null,
  currentApr: loan.currentApr ?? null,
  currentPrimeRate: loan.currentPrimeRate ?? null,
  isVariableApr: loan.isVariableApr ?? null,

  isDelinquent: loan.isDelinquent ?? null,
  daysDelinquent: loan.daysDelinquent ?? null,
  delinquentSince: loan.delinquentSince ?? null,

  gracePeriodDays: loan.program?.paymentDueDateGracePeriodDays ?? null,

  users: (loan.users ?? []).filter((user): user is GeneratedUser => Boolean(user)).map(toLoanUserListItem),
  cards: (loan.cards ?? []).filter((card): card is GeneratedCard => Boolean(card)).map(toCardViewModel),
});
